import * as React from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { Loader2 } from "lucide-react";

/**
 * Global navigation loader.
 *
 * Two problems this solves:
 *
 *   1. When a user clicks a link, there's a brief gap between the click and
 *      the next page actually rendering (lazy chunk + initial data fetch).
 *      During that gap the user can click the same link a second time, or
 *      click into a different menu item, kicking off a competing navigation
 *      that races against the first one. This component shows a full-screen
 *      overlay that intercepts pointer events while a transition is in
 *      flight, eliminating the double-click race.
 *
 *   2. The user gets clear visual feedback that "something is happening"
 *      — a brand-tinted spinner above a soft backdrop-blurred backdrop —
 *      instead of seeing the previous page sit there frozen for a moment.
 *
 * How it works:
 *
 *   - A document-level click listener detects clicks on internal links
 *     (anchor elements with href) and items tagged data-nav-trigger. When
 *     a real navigation is about to happen, the loader appears immediately
 *     (synchronously with the click, before any React state settles).
 *
 *   - A useLocation effect watches the pathname/search. When it changes,
 *     the new route has mounted — we hold the loader visible for one more
 *     ~200ms tick (so even instant transitions get a visible signal), then
 *     fade it out.
 *
 *   - A safety timeout always force-hides the loader after 5 seconds even
 *     if pathname never changes (e.g. the click went to a same-route
 *     anchor and we mis-detected). The loader is a UX affordance, not a
 *     hard block — it must never get stuck.
 */
const HIDE_DELAY_MS = 220;
const SAFETY_TIMEOUT_MS = 5000;
// How long after show() to keep watching for a modal that intercepts the
// navigation. Unsaved-changes / confirmation guards open their modal
// synchronously in the link's onClick handler; by ~300ms after the click
// either the modal is visible or the navigation went through.
const MODAL_GUARD_WINDOW_MS = 300;
// Selectors that match an open dialog overlay. If any of these are in
// the DOM right after show(), the navigation was intercepted by a
// modal (unsaved-changes guard, "are you sure?" confirm, etc.) and the
// loader should hide — the user is now interacting with the modal,
// not waiting for a route to load.
const OPEN_MODAL_SELECTORS = [
  ".ant-modal-wrap:not([style*='display: none'])",
  ".ant-modal-root .ant-modal-mask:not([style*='display: none'])",
  '[role="dialog"][aria-modal="true"]',
].join(",");

// Module-level imperative trigger. Programmatic navigations (the sidebar's
// nav(path) helper, header back buttons, action-bar buttons that call
// useNavigate) don't render <a href> elements, so the document-level click
// listener can't catch them. Those callers import triggerNavLoader and
// invoke it right before navigate() — the loader appears synchronously
// during the lazy-chunk load gap.
//
// NavigationLoader registers/unregisters its show() function on mount/
// unmount; before mount or after unmount this is a safe no-op.
let registeredShow: (() => void) | null = null;

export function triggerNavLoader(targetPath?: string): void {
  // If the caller passes a target and it resolves to the current URL,
  // skip the loader entirely — clicking a link for the page you're
  // already on is a no-op, not a re-load. Callers can omit the
  // argument when the target is unknown (the document-level click
  // capture handles its own same-URL check elsewhere).
  if (targetPath && typeof window !== "undefined") {
    const normalize = (p: string) => p.replace(/\/+$/, "") || "/";
    const current = normalize(window.location.pathname);
    const resolved = targetPath.startsWith("/")
      ? normalize(targetPath)
      : null;
    if (resolved && resolved === current) return;
  }
  registeredShow?.();
}

function isInternalLink(href: string | null): boolean {
  if (!href) return false;
  // Hash links, mailto, tel, external — none should trigger a route change
  if (
    href.startsWith("#") ||
    href.startsWith("mailto:") ||
    href.startsWith("tel:") ||
    /^https?:\/\//i.test(href)
  ) {
    return false;
  }
  // javascript:; etc. — skip
  if (href.startsWith("javascript:")) return false;
  return true;
}

export default function NavigationLoader() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const [isVisible, setIsVisible] = React.useState(false);

  // Track the path we are currently displaying so we can detect "the
  // navigation actually completed" (pathname changed since we showed
  // the loader).
  const pathAtShowRef = React.useRef<string | null>(null);
  const hideTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const safetyTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const modalGuardRef = React.useRef<MutationObserver | null>(null);
  const modalGuardTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const currentKey = `${location.pathname}${location.search}`;

  const clearTimers = React.useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    if (safetyTimerRef.current) {
      clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = null;
    }
    if (modalGuardTimerRef.current) {
      clearTimeout(modalGuardTimerRef.current);
      modalGuardTimerRef.current = null;
    }
    if (modalGuardRef.current) {
      modalGuardRef.current.disconnect();
      modalGuardRef.current = null;
    }
  }, []);

  const hideImmediately = React.useCallback(() => {
    setIsVisible(false);
    pathAtShowRef.current = null;
    clearTimers();
  }, [clearTimers]);

  const show = React.useCallback(() => {
    clearTimers();

    // If a modal is ALREADY open when the click happens (e.g. user
    // clicked a link inside an existing modal that itself opens a
    // confirmation), don't show the loader — the click is happening
    // inside modal context, not a top-level navigation.
    if (typeof document !== "undefined") {
      const alreadyOpen = document.querySelector(OPEN_MODAL_SELECTORS);
      if (alreadyOpen) {
        // Still show briefly so the user gets feedback, but the modal-
        // watch effect below will tear it down within a few ms if the
        // modal stays open. For now: just bail entirely. The modal IS
        // the visible feedback.
        return;
      }
    }

    pathAtShowRef.current = currentKey;
    setIsVisible(true);

    // Watch for a modal appearing within MODAL_GUARD_WINDOW_MS. If one
    // does, the navigation was blocked by an unsaved-changes guard or
    // confirmation prompt — the user is now interacting with the
    // modal, not waiting for a route to load. Hide the loader so it
    // doesn't sit on top of the modal until the safety timeout fires.
    if (typeof document !== "undefined" && typeof MutationObserver !== "undefined") {
      const observer = new MutationObserver(() => {
        const modal = document.querySelector(OPEN_MODAL_SELECTORS);
        if (modal) {
          hideImmediately();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
      modalGuardRef.current = observer;
      modalGuardTimerRef.current = setTimeout(() => {
        if (modalGuardRef.current) {
          modalGuardRef.current.disconnect();
          modalGuardRef.current = null;
        }
        modalGuardTimerRef.current = null;
      }, MODAL_GUARD_WINDOW_MS);
    }

    // Safety: never let the loader live past SAFETY_TIMEOUT_MS even if the
    // route never changes (e.g. anchor link, blocked navigation, error).
    safetyTimerRef.current = setTimeout(() => {
      setIsVisible(false);
      pathAtShowRef.current = null;
    }, SAFETY_TIMEOUT_MS);
  }, [currentKey, clearTimers, hideImmediately]);

  // Document-level click capture for link navigations. Using the capture
  // phase ensures we run before React Router's own click handlers — the
  // loader is visible by the time the route swap starts. We don't
  // preventDefault; we just observe.
  React.useEffect(() => {
    function onClick(e: MouseEvent) {
      // Bail on modified clicks — those open new tabs / windows and don't
      // navigate the current document. Same for non-primary buttons.
      if (
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey ||
        e.defaultPrevented
      ) {
        return;
      }

      const target = e.target as Element | null;
      if (!target) return;

      // Skip clicks happening inside a storefront preview / template
      // renderer or any element opted out via data-nav-loader-ignore.
      // Links inside a rendered storefront ("Home", "Shop", "Shop Now",
      // navbar/category/product links, etc.) are demo links — in the gallery
      // preview AND the template editor canvas they don't navigate the merchant
      // dashboard anywhere (the editor even preventDefaults them), so firing the
      // global loader just leaves it stuck until the safety timeout. The editor
      // canvas is marked with [data-storefront-renderer]; the gallery preview
      // with .storefront-preview-canvas.
      const inIgnoredZone = target.closest(
        ".storefront-preview-canvas, [data-storefront-renderer], [data-nav-loader-ignore]",
      );
      if (inIgnoredZone) return;

      // Look for the nearest navigation trigger — either an <a href> or
      // any element opted in via data-nav-trigger (used by buttons that
      // navigate programmatically via useNavigate).
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      const trigger = target.closest("[data-nav-trigger]") as HTMLElement | null;

      let href: string | null = null;
      if (anchor) {
        // Read the raw href attribute so we can detect external/hash/mailto
        // before the browser resolves it.
        href = anchor.getAttribute("href");
        if (!isInternalLink(href)) return;
        // anchor target="_blank" opens in a new tab — no current-document
        // navigation, no loader.
        if (anchor.target && anchor.target !== "" && anchor.target !== "_self")
          return;
        // Same URL as current location → no actual navigation
        if (anchor.pathname === location.pathname && anchor.search === location.search)
          return;
      } else if (trigger) {
        // Programmatic navigation marker — we don't know the target URL,
        // but the consumer has opted in, so show the loader.
      } else {
        return;
      }

      show();
    }

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [location.pathname, location.search, show]);

  // Browser back/forward → location updates but no click event fires.
  // useNavigationType lets us detect POP and show the loader briefly.
  //
  // POP differs from a forward link click in a critical way: by the time
  // this effect runs, the URL has ALREADY changed. The standard hide
  // path (below) waits for `pathAtShowRef.current !== currentKey` — i.e.
  // for the URL to change AFTER show(). For POP that condition is true
  // the instant show() runs (pathAtShowRef captures the new URL), so
  // the regular hide effect short-circuits and the loader sits visible
  // until the 5s safety timeout — making "back" from a detail page feel
  // broken even when React Query has the list cached. Schedule the hide
  // explicitly here so POP gets just a brief flash, not a 5-second hang.
  const lastKeyRef = React.useRef(currentKey);
  React.useEffect(() => {
    if (lastKeyRef.current === currentKey) return;
    if (navigationType === "POP" && !isVisible) {
      show();
      const t = setTimeout(() => {
        setIsVisible(false);
        pathAtShowRef.current = null;
      }, HIDE_DELAY_MS);
      lastKeyRef.current = currentKey;
      return () => clearTimeout(t);
    }
    lastKeyRef.current = currentKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey, navigationType]);

  // When the displayed path changes, the new route has mounted — start
  // the hide timer so the spinner is visible for HIDE_DELAY_MS even on
  // instant transitions, then fade out.
  React.useEffect(() => {
    if (!isVisible) return;
    if (pathAtShowRef.current === null) return;
    if (pathAtShowRef.current === currentKey) return;

    // Route changed since we showed the loader → schedule hide
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      setIsVisible(false);
      pathAtShowRef.current = null;
      if (safetyTimerRef.current) {
        clearTimeout(safetyTimerRef.current);
        safetyTimerRef.current = null;
      }
    }, HIDE_DELAY_MS);

    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
  }, [currentKey, isVisible]);

  // Register/unregister the module-level imperative trigger so
  // triggerNavLoader() works from anywhere in the app while the component
  // is mounted.
  React.useEffect(() => {
    registeredShow = show;
    return () => {
      registeredShow = null;
    };
  }, [show]);

  // Cleanup on unmount
  React.useEffect(() => () => clearTimers(), [clearTimers]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          key="nav-loader"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          role="status"
          aria-live="polite"
          aria-label="Loading next page"
          // z-[999999] keeps the loader above modals, drawers, and the
          // floating preferences dropdown. pointer-events on the backdrop
          // intercepts every click — that's how we kill the double-click
          // race while a route is in flight.
          className="fixed inset-0 z-[999999] flex items-center justify-center"
          style={{
            background: "rgba(15, 23, 42, 0.18)",
            backdropFilter: "blur(2px)",
            WebkitBackdropFilter: "blur(2px)",
          }}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.98, opacity: 0 }}
            transition={{ duration: 0.18, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="flex flex-col items-center gap-3 rounded-xl px-6 py-5"
            style={{
              background: "var(--color-surface-elevated)",
              border:
                "1px solid color-mix(in srgb, var(--brand-primary) 14%, var(--color-border))",
              boxShadow:
                "0 10px 30px rgba(15,118,110,0.12), 0 2px 8px rgba(0,0,0,0.08)",
              minWidth: 140,
            }}
          >
            <Loader2
              className="w-7 h-7 animate-spin"
              style={{ color: "var(--brand-primary)" }}
              aria-hidden
            />
            <span
              className="text-eyebrow"
              style={{ color: "var(--color-text-secondary)" }}
            >
              Loading
            </span>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
